export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

const COMMON_PASSWORDS = new Set([
  'password',
  'password1',
  'password123',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty',
  'qwerty123',
  'qwertyuiop',
  'abc12345',
  'abcd1234',
  'iloveyou',
  'letmein',
  'welcome',
  'welcome1',
  'admin',
  'admin123',
  'administrator',
  'monkey',
  'dragon',
  'sunshine',
  'princess',
  'football',
  'baseball',
  'master',
  'shadow',
  'superman',
  'batman',
  'trustno1',
  'passw0rd',
  'p@ssw0rd',
  'p@ssword',
  'changeme',
  'starwars',
  'hello123',
  'test1234',
  'qazwsxedc',
  '1qaz2wsx',
  'easypeasy',
  'easy-peasy',
  'easypeasy.ai',
  'easypeasyai',
]);

export type PasswordRuleKey =
  | 'length'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'special'
  | 'notCommon'
  | 'noEmail';

export interface PasswordRule {
  key: PasswordRuleKey;
  label: string;
  passed: boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  rules: PasswordRule[];
  firstError: string | null;
}

const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_\-+=[\]{}|\\:;"'<>,.?/~`]/;

const stripNonAlnum = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

export function validatePassword(
  password: string,
  email?: string,
): PasswordValidationResult {
  const trimmed = password ?? '';
  const normalized = trimmed.toLowerCase();
  const stripped = stripNonAlnum(trimmed);

  const emailLocalPart = email ? email.split('@')[0]?.toLowerCase() : '';
  const sharesEmail =
    !!emailLocalPart &&
    emailLocalPart.length >= 4 &&
    normalized.includes(emailLocalPart);

  const isCommon =
    COMMON_PASSWORDS.has(normalized) ||
    COMMON_PASSWORDS.has(stripped) ||
    /^(.)\1+$/.test(trimmed) ||
    /^(?:0123456789|1234567890|abcdefghij|qwertyuiop)/.test(normalized);

  const rules: PasswordRule[] = [
    {
      key: 'length',
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
      passed:
        trimmed.length >= PASSWORD_MIN_LENGTH &&
        trimmed.length <= PASSWORD_MAX_LENGTH,
    },
    {
      key: 'uppercase',
      label: 'One uppercase letter (A–Z)',
      passed: /[A-Z]/.test(trimmed),
    },
    {
      key: 'lowercase',
      label: 'One lowercase letter (a–z)',
      passed: /[a-z]/.test(trimmed),
    },
    {
      key: 'number',
      label: 'One number (0–9)',
      passed: /[0-9]/.test(trimmed),
    },
    {
      key: 'special',
      label: 'One special character (e.g. !@#$%^&*)',
      passed: SPECIAL_CHARS_REGEX.test(trimmed),
    },
    {
      key: 'notCommon',
      label: 'Not a common or easily guessable password',
      passed: trimmed.length > 0 && !isCommon,
    },
    {
      key: 'noEmail',
      label: 'Does not contain your email address',
      passed: !sharesEmail,
    },
  ];

  const failed = rules.find((rule) => !rule.passed);

  return {
    isValid: rules.every((rule) => rule.passed),
    rules,
    firstError: failed
      ? failed.key === 'length'
        ? `Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`
        : failed.key === 'notCommon'
          ? 'This password is too common. Please choose a less predictable password.'
          : failed.key === 'noEmail'
            ? 'Password must not contain your email address.'
            : `Password must include ${failed.label.toLowerCase()}.`
      : null,
  };
}
